"use client";
import { client } from "@/sanity/client";
import imageUrlBuilder from "@sanity/image-url";
import { SanityImageSource } from "@sanity/image-url/lib/types/types";
import { ArrowRight } from "lucide-react";
import type { SanityDocument } from "next-sanity";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

const builder = imageUrlBuilder(client);
const urlFor = (source: SanityImageSource) => builder.image(source);

const POSTS_QUERY = `*[
  _type == "blogPost"
  && defined(slug.current)
]|order(publishedAt desc)[0...7]{
  _id,
  title,
  content,
  slug,
  mainImage,
  readingTime,
  publishedAt,
  categories[]->{
    _id,
    title,
    slug
  }
}`;

const options = { next: { revalidate: 86400 } };

const Blogs = () => {
  const [postList, setPostList] = useState([] as SanityDocument[]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    async function getPosts() {
      const posts = await client.fetch<SanityDocument[]>(
        POSTS_QUERY,
        {},
        options,
      );
      setPostList(posts || []);
    }
    getPosts();

    setIsMobile(window.innerWidth < 768);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="relative">
      {/* <span className="w-[30%] h-[80%] absolute left-[-5%] top-[-20%]">
        <BlueGlow />
      </span>
      <span className="w-[80%] h-[80%] absolute right-[-20%] bottom-[-45%]">
        <PinkGlow />
      </span> */}
      <div className="container max-w-[1400px] mx-auto pb-10 sm:pb-20 px-4 xxs:px-6 sm:px-[40px]">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-[20px] sm:text-3xl md:text-4xl lg:text-[46px] leading-[1.2]">
              Level up with our{" "}
              <span className="bg-gradient-to-r from-[#1776F0] to-[#8F3EBC] bg-clip-text text-transparent inline-block">
                featured blogs
              </span>
            </h2>
            <p className="text-[#4B5563] font-semibold text-sm md:text-lg lg:text-xl xl:text-[23px] ff_poppins mt-1">
              Fresh updates and insights to keep your journey on track.
            </p>
          </div>
          <Link
            href={"/blogs"}
            className="lg:flex items-center gap-[11px] hidden "
          >
            <p className="font-semibold text-[17px] text-[#0B57D0] ff_poppins">
              View all blogs
            </p>
            <ArrowRight className="text-[#0B57D0]" />
          </Link>
        </div>
        {postList && postList.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mt-6 md:mt-8 lg:mt-10 xl:mt-[48px]">
            {/* Featured Card */}
            <Link
              href={`/blogs/${postList[0].slug?.current}`}
              className="xl:col-span-2 bg-blue-600 rounded-2xl p-5 md:p-6 lg:p-7 xl:p-[30px] text-white flex flex-col md:flex-row gap-4 shadow-lg"
            >
              <Image
                width={315}
                height={298}
                src={
                  postList[0].mainImage
                    ? urlFor(postList[0].mainImage).width(315).height(298).url()
                    : ""
                }
                alt="blog"
                className="md:w-[40%] rounded-[15px] object-cover shrink-0"
              />

              <div className="flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    {postList[0].categories &&
                      postList[0].categories.map(
                        (category: { title: string }, index: number) => {
                          return (
                            <label
                              key={index}
                              className="inline-block px-2 text-[#0B57D0] bg-white py-1 rounded-full text-base font-bold"
                            >
                              {category.title}
                            </label>
                          );
                        },
                      )}
                  </div>

                  <h3 className="mt-[19px] font-bold text-xl sm:text-[23px] text-white leading-[30px]">
                    {postList[0].title}
                  </h3>

                  <p className="mt-2 text-sm sm:text-lg font-medium ff_poppins text-white leading-[30px]">
                    {postList[0].description}
                  </p>
                </div>

                <p className="text-sm md:text-base lg:text-lg font-medium ff_poppins text-white mt-4">
                  {new Date(postList[0].publishedAt).toLocaleDateString()}
                </p>
              </div>
            </Link>

            {/* Normal Cards */}
            {postList.slice(1, isMobile ? 2 : 7).map((blog, idx) => {
              const postImageUrl = blog.mainImage
                ? urlFor(blog.mainImage).width(550).height(310).url()
                : null;
              return (
                <Link
                  href={`/blogs/${blog.slug?.current}`}
                  key={idx}
                  className="bg-white border border-gray-200 rounded-2xl p-[22px] shadow-sm hover:shadow-md transition"
                >
                  <Image
                    width={315}
                    height={156}
                    src={postImageUrl || ""}
                    alt={blog.title}
                    className="w-full h-40 rounded-xl object-cover"
                  />

                  <div className="mt-3">
                    <div className="flex items-center gap-2 mt-4 flex-wrap">
                      {blog.categories &&
                        blog.categories.map(
                          (category: { title: string }, index: number) => {
                            return (
                              <label
                                key={index}
                                className="inline-block px-1 text-[#0B57D0] text-base font-bold"
                              >
                                {category.title}
                              </label>
                            );
                          },
                        )}
                    </div>

                    <h3 className="mt-1 text-lg font-semibold line-clamp-2">
                      {blog.title}
                    </h3>

                    <p className="mt-2 text-xs sm:text-sm text-gray-600 line-clamp-3">
                      {blog.description}
                    </p>

                    <p className="mt-4 text-xs text-gray-500">
                      {new Date(blog.publishedAt).toLocaleDateString()}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;
